<script>
  import { onMount, onDestroy } from 'svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '../stores/selectionStore';
  import { userRole, userRoles, userReadOnly } from '../stores/sessionStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { firstInfoCache, appInfoCache, appDeviceCache } from '../stores/cacheStore';
  import { studyProperties, isFeatureEnabled } from '../stores/configStore';
  import { isCompactMode } from '../stores/uiStore';
  import ParticipantFiles from './ParticipantFiles.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
  import { faPaperclip, faPencil, faHistory, faCircleInfo, faUsers } from '@fortawesome/pro-light-svg-icons';
  import { fade, slide } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();

  // Prop to control whether the options menu is shown
  export let showOptionsMenu = false;

  let localTime = '';
  let firstInfo = [];
  let appInfo = [];
  let appDevice = [];
  let timeInterval;
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  let showFiles = false;
  let showMenu = false;
  let isParticipantFilesEnabled = false;
  let showEligibleConfirm = false;
  let isPlatformInfoLoading = false;

  $: if ($studyProperties?.features?.documentManagement?.participant !== undefined) {
    isParticipantFilesEnabled = $studyProperties.features.documentManagement.participant;
  }

  $: if (isParticipantFilesEnabled && $studyProperties?.features?.documentManagement?.autoShowParticipant) {
    showFiles = true;
  }

  // Function to update local time
  const updateLocalTime = () => {
    if ($selectedParticipant && $selectedParticipant.timezoneCode) {
      const options = {
        timeZone: $selectedParticipant.timezoneCode,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      const formatter = new Intl.DateTimeFormat([], options);
      localTime = formatter.format(new Date());
    }
  };

  // Function to format randomization date
  const formatRandomizationDate = (dateString) => {
    if (!dateString) return 'n/a';
    try {
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };
      const formatter = new Intl.DateTimeFormat([], options);
      return formatter.format(date);
    } catch (error) {
      return 'n/a';
    }
  };

  async function fetchFirstInfo(participantId) {
    try {
      // Check cache first
      const cached = $firstInfoCache.get(participantId);
      if (cached) {
        if ($selectedParticipant?._id === participantId) {
          firstInfo = cached;
        }
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/logEntries?page=1&pageSize=1&subjectGUID=${participantId}&recent=false`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch first info');
      }

      const data = await res.json();
      const result = data.data || [];

      firstInfoCache.update((cache) => {
        cache.set(participantId, result);
        return cache;
      });

      if ($selectedParticipant?._id === participantId) {
        firstInfo = result;
      }
    } catch (err) {
      console.error('Error fetching first info:', err);
      if ($selectedParticipant?._id === participantId) {
        firstInfo = [];
      }
    }
  }

  async function fetchAppInfo(participantId) {
    try {
      const cached = $appInfoCache.get(participantId);
      if (cached) {
        if ($selectedParticipant?._id === participantId) {
          appInfo = cached;
        }
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/logEntries?page=1&pageSize=1&event=APP_VERSION&subjectGUID=${participantId}&recent=false`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch app info');
      }

      const data = await res.json();
      const result = data.data || [];

      appInfoCache.update((cache) => {
        cache.set(participantId, result);
        return cache;
      });

      if ($selectedParticipant?._id === participantId) {
        appInfo = result;
      }
    } catch (err) {
      console.error('Error fetching app info:', err);
      if ($selectedParticipant?._id === participantId) {
        appInfo = [];
      }
    }
  }

  async function fetchAppDevice(participantId) {
    try {
      const cached = $appDeviceCache.get(participantId);
      if (cached) {
        if ($selectedParticipant?._id === participantId) {
          appDevice = cached;
        }
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/logEntries?page=1&pageSize=1&event=APP_DEVICE&subjectGUID=${participantId}&recent=false`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch app info');
      }

      const data = await res.json();
      const result = data.data || [];

      appDeviceCache.update((cache) => {
        cache.set(participantId, result);
        return cache;
      });

      if ($selectedParticipant?._id === participantId) {
        appDevice = result;
      }
    } catch (err) {
      console.error('Error fetching app info:', err);
      if ($selectedParticipant?._id === participantId) {
        appDevice = [];
      }
    }
  }

  async function loadParticipantPlatformInfo(participantId) {
    isPlatformInfoLoading = true;
    firstInfo = [];
    appInfo = [];
    appDevice = [];

    await Promise.allSettled([
      fetchFirstInfo(participantId),
      fetchAppInfo(participantId),
      fetchAppDevice(participantId)
    ]);

    if ($selectedParticipant?._id === participantId) {
      isPlatformInfoLoading = false;
    }
  }

  let showPassword = false;
  let showId = false;
  let showLoginInfo = false;

  $: canEditParticipant = hasAccessUiAction(
    'participants.details.edit',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canViewAuditLogs = hasAccessUiAction(
    'participants.details.viewAuditLogs',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canSeePIN = hasAccessUiAction(
    'participants.details.viewPin',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canSeeId = hasAccessUiAction(
    'participants.details.viewSubjectId',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canManageFormProxies = hasAccessUiAction(
    'participants.details.manageFormProxies',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: isUpgradeEligibilityEnabled = isFeatureEnabled('upgradeEligibility', $studyProperties);
  $: canToggleEligibility = hasAccessUiAction(
    'participants.details.toggleEligibility',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Helper function to check if user has required role
  const hasRequiredRole = () => {
    //console.log("userRole: ", $userRole);
    return $userRole === 'admin' || $userRole === 'support' || $userRole === 'site';
  };

  function handleMenuClick(event) {
    event.stopPropagation();
    showMenu = !showMenu;
  }

  function handleMenuClose() {
    showMenu = false;
  }

  function promptEligibleToggle() {
    showEligibleConfirm = true;
  }

  async function confirmEligibleToggle() {
    if (!$selectedParticipant?._id) return;

    const newValue = !$selectedParticipant.upgradeEligible;

    try {
      const toggleRes = await fetch(`${API_BASE_URL}/persons/${$selectedParticipant._id}/upgradeEligible`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upgradeEligible: newValue })
      });

      if (!toggleRes.ok) {
        const errorData = await toggleRes.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update eligibility');
      }

      selectedParticipant.update((participant) =>
        participant
          ? {
              ...participant,
              upgradeEligible: newValue,
              ...(newValue
                ? {
                    properties: {
                      ...(participant.properties || {}),
                      syncProvider: 'powerSync'
                    }
                  }
                : {})
            }
          : participant
      );
    } catch (err) {
      console.error('Error toggling upgrade eligibility:', err);
    } finally {
      showEligibleConfirm = false;
    }
  }

  function cancelEligibleToggle() {
    showEligibleConfirm = false;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  }

  onMount(() => {
    timeInterval = setInterval(updateLocalTime, 1000);
    document.addEventListener('click', handleClickOutside);
    return () => {
      clearInterval(timeInterval);
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Reactive statement to fetch data when participant changes
  $: if ($selectedParticipant?._id) {
    const participantId = $selectedParticipant._id;
    loadParticipantPlatformInfo(participantId);
    updateLocalTime();
  }



  // Initialize local time on mount
  updateLocalTime();
</script>

{#if $selectedParticipant}
  <div class="mx-1 mt-3 mb-0 p-4 rounded-lg shadow-md overflow-y-auto relative">
    <div
      class="absolute inset-0 bg-[url('/title_bg_light.jpg')] dark:bg-[url('/title_bg_dark.jpg')] bg-cover bg-center bg-no-repeat opacity-50 dark:opacity-75"
    ></div>

    <!-- Vertical Participant Label -->
    <div
      class="absolute left-0 top-0 bottom-0 flex items-center justify-center w-8 z-20 bg-gray-100 dark:bg-gray-800"
    >
      <div
        class="writing-mode-vertical text-xs font-black text-gray-600 dark:text-gray-300 tracking-wider"
      >
        {#if !$isCompactMode}
          PARTICIPANT
        {:else}
          PART.
        {/if}
      </div>
    </div>

    <div class="relative z-10 ml-8">
      {#if showOptionsMenu}
        <div class="menu-container absolute right-1 top-0 flex-none">
          <button
            on:click={handleMenuClick}
            class="w-8 h-8 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:text-sky-800 dark:hover:text-white/70"
            title="More options"
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
          {#if showMenu}
            <div
              class="fixed transform -translate-x-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-[9999]"
            >
              {#if isParticipantFilesEnabled}
                <button
                  on:click={() => {
                    showFiles = !showFiles;
                    handleMenuClose();
                  }}
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                >
                  <span>{showFiles ? 'Hide documents' : 'Show documents'}</span>
                  <FontAwesomeIcon icon={faPaperclip} class="text-sky-800 dark:text-white/70" />
                </button>
              {/if}
              <button
                on:click={() => {
                  console.log('Edit button clicked');
                  dispatch('edit', $selectedParticipant);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canEditParticipant}
                class:opacity-50={!canEditParticipant}
              >
                <span>Edit participant</span>
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => {
                  console.log('Audit log button clicked');
                  dispatch('auditLog', $selectedParticipant);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canViewAuditLogs}
                class:opacity-50={!canViewAuditLogs}
              >
                <span>View audit logs</span>
                <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
              </button>
              {#if canManageFormProxies && $studyProperties?.features?.diary === true}
              <button
                on:click={() => {
                  console.log('Subject form proxies button clicked');
                  dispatch('subjectFormProxies', $selectedParticipant);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canEditParticipant}
                class:opacity-50={!canEditParticipant}
              >
                <span>Form proxies</span>
                <FontAwesomeIcon icon={faUsers} class="text-sky-800 dark:text-white/70" />
              </button>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
      <div class="grid grid-cols-5 print:grid-cols-3 gap-3 mt-0">
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Subject ID</label>
          <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedParticipant.subjectId}</div>
        </div>
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Timezone</label>
          <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
            {$selectedParticipant.timezoneCode}
          </div>
        </div>
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Local Time</label>
          <div class="text-gray-900 dark:text-gray-100 print:w-52 whitespace-nowrap truncate">{localTime}</div>
        </div>
        {#if firstInfo[0] && firstInfo[0].platform == 'ios'}
          <div class="col-span-2 print:hidden pr-14">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">App Version</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
              {firstInfo[0]
                ? firstInfo[0].context.appVersion +
                  ' (' +
                  firstInfo[0].context.buildNumber +
                  ') iOS: ' +
                  firstInfo[0].context.iosVersion
                : ''}
            </div>
          </div>
        {:else if appInfo[0] || (!isPlatformInfoLoading && isUpgradeEligibilityEnabled && canToggleEligibility)}
          <div class="col-span-2 print:hidden pr-14">
            <div class="flex max-w-full items-start gap-6">
              <div class="min-w-0 flex-1">
                {#if appInfo[0]}
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">App Version</label>
                  <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
                    {appInfo[0] ? appInfo[0].message.replace('SUMMA Mobile', '') : ''}
                    {appDevice[0] ? appDevice[0].message.replace(', OS', '') : ''}
                  </div>
                {/if}
              </div>
              {#if !isPlatformInfoLoading && isUpgradeEligibilityEnabled && canToggleEligibility}
                <div class="shrink-0 flex-1">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Eligible</label>
                  <button
                    on:click={promptEligibleToggle}
                    class="text-xs px-2 py-0.5 rounded-full ring-1 ring-inset {($selectedParticipant?.upgradeEligible ? 'text-green-600 ring-green-600' : 'text-gray-500 ring-gray-400')}"
                    title={$selectedParticipant?.upgradeEligible ? 'Click to remove eligibility' : 'Click to mark eligible'}
                  >
                    {$selectedParticipant?.upgradeEligible ? 'Yes' : 'No'}
                  </button>
                </div>
              {/if}
            </div>
          </div>
        {/if}
      </div>
      {#if !$isCompactMode}
        <div class="grid grid-cols-5 print:grid-cols-3 gap-3 mt-2">
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Status</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedParticipant.statusLabel}</div>
          </div>
          {#if isFeatureEnabled('randomization.enabled', $studyProperties)}
            <div>
              <!-- svelte-ignore a11y-label-has-associated-control -->
              <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Planned Randomisation</label>
              <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
                {formatRandomizationDate($selectedParticipant.randomizationDate)}
              </div>
            </div>
          {:else}
            <div>
              <!-- svelte-ignore a11y-label-has-associated-control -->
              <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">&nbsp;</label>
              <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{''}</div>
            </div>
          {/if}
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Language</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
              {$selectedParticipant.language ? `${$selectedParticipant.flag || ''} ${$selectedParticipant.language}`.trim() : 'n/a'}
            </div>
          </div>

          <div class="col-span-2">
            <div class="grid grid-cols-4 gap-3 print:hidden">
              {#if canSeePIN}
              <div>
                <!-- svelte-ignore a11y-label-has-associated-control -->
                <label class="block text-xs text-gray-900 dark:text-gray-200 items-center gap-1 whitespace-nowrap truncate">
                  Mobile PIN
                  <button
                    class="text-sky-800 dark:text-white/70 hover:text-sky-600 dark:hover:text-white/90 flex-shrink-0"
                    on:click={() => (showLoginInfo = !showLoginInfo)}
                    title="Show login information"
                  >
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </button>
                </label>
                <div class="text-gray-900 dark:text-gray-100 relative">
                  <span
                    role="button"
                    tabindex="0"
                    class="cursor-pointer whitespace-nowrap truncate"
                    on:mouseenter={() => (showPassword = true)}
                    on:mouseleave={() => (showPassword = false)}
                    on:keydown={(e) => e.key === 'Enter' && (showPassword = !showPassword)}
                  >
                    {#if showPassword}
                      {$selectedParticipant.password || ''}
                    {:else}
                      {'•'.repeat($selectedParticipant.password?.length || 0)}
                    {/if}
                  </span>
                  </div>
                </div>
              {/if}
              {#if canSeeId}
                <div class="col-span-3">
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Id</label>
                  <div class="text-gray-900 dark:text-gray-100">
                    <span
                      role="button"
                      tabindex="0"
                      class="cursor-pointer whitespace-nowrap truncate"
                      on:mouseenter={() => (showId = true)}
                      on:mouseleave={() => (showId = false)}
                      on:keydown={(e) => e.key === 'Enter' && (showId = !showId)}
                    >
                      {#if showId}
                        {$selectedParticipant._id || ''}
                      {:else}
                        {'•'.repeat($selectedParticipant._id?.length || 0)}
                      {/if}
                    </span>
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>

  {#if showLoginInfo}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
      on:click={() => (showLoginInfo = false)}
    >
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        class="bg-transparent p-0 rounded-lg shadow-none max-w-md w-full mx-4 flex flex-col items-center"
        on:click|stopPropagation
      >
        <div class="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg p-0">
          <div class="px-6 pt-6 pb-6">
            <div class="text-xs font-bold uppercase text-gray-500 tracking-wider mb-3">
              USERNAME / PASSCODE
            </div>
            <div
              class="bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <div class="text-xs text-gray-400 mb-1">Protocol ID</div>
                <div class="text-base text-gray-900 dark:text-white">
                  {$selectedStudy.protocolNumber}
                </div>
              </div>
              <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <div class="text-xs text-gray-400 mb-1">Subject ID</div>
                <div class="text-base text-gray-900 dark:text-white">
                  {$selectedParticipant.subjectId}
                </div>
              </div>
              <div class="px-4 py-3">
                <div class="text-xs text-gray-400 mb-1">Passcode</div>
                <div class="text-base text-gray-900 dark:text-white">
                  {$selectedParticipant.password || ''}
                </div>
              </div>
            </div>
          </div>
        </div>
        <button
          class="mt-4 w-full max-w-md bg-sky-800 text-white py-2 px-4 rounded-lg hover:bg-sky-700 dark:bg-sky-700 dark:hover:bg-sky-600"
          on:click={() => (showLoginInfo = false)}
        >
          Close
        </button>
      </div>
    </div>
  {/if}

  {#if showEligibleConfirm}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={cancelEligibleToggle}></div>
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 z-10 max-w-sm w-full mx-4">
        <h2 class="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Confirm eligibility change
        </h2>
        <p class="mb-4 text-sm text-gray-700 dark:text-gray-300">
          {#if $selectedParticipant?.upgradeEligible}
            Remove upgrade eligibility for participant <span class="font-mono">{$selectedParticipant.subjectId}</span>?
          {:else}
            Mark participant <span class="font-mono">{$selectedParticipant?.subjectId}</span> as upgrade eligible?
          {/if}
        </p>
        <div class="flex justify-end gap-2">
          <button on:click={cancelEligibleToggle} class="bg-gray-500 text-white py-1 px-3 rounded hover:bg-gray-700 text-sm">Cancel</button>
          <button on:click={confirmEligibleToggle} class="bg-sky-700 text-white py-1 px-3 rounded hover:bg-sky-800 text-sm">Confirm</button>
        </div>
      </div>
    </div>
  {/if}

  {#if showFiles}
    <div class="mx-1 mt-3" in:fade={{ duration: 200 }} out:fade={{ duration: 200 }}>
      <div in:slide={{ duration: 200 }} out:slide={{ duration: 200 }}>
        <ParticipantFiles on:close={() => (showFiles = false)} />
      </div>
    </div>
  {/if}
{/if}

<style>
  .writing-mode-vertical {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
  }
</style>
